import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import api from "../util/api";

export const fetchCredentials = createAsyncThunk("credentials/fetchCredentials", async () => {
    const response = await api.get(`v2/api/settings/credentials`);
    return response.data;
});

export const addNewCredential = createAsyncThunk("credentials/addNewCredential", async credential => {
    const response = await api.post("v2/api/settings/credentials", {credential});
    return response.data;
});

export const updateCredential = createAsyncThunk("credentials/updateCredential", async credential => {
    const response = await api.put(`v2/api/settings/credentials/${credential.credential_id}`, {
        credential,
    });
    return response.data;
});

export const deleteCredential = createAsyncThunk("credentials/deleteCredential", async credentialId => {
    const response = await api.delete(`v2/api/settings/credentials/${credentialId}`);
    return response.data;
});

export const fetchGroups = createAsyncThunk("credentials/etGroups", async () => {
    const response = await api.get("v2/api/settings/credentials/groups");
    return response.data;
});

export const validateNewEmails = createAsyncThunk("credentials/validateEmails", async emails => {
    const response = await api.post("v2/api/settings/credentials/invite/validate", {emails});
    return response.data;
});

export const executeInvitaions = createAsyncThunk("credentials/executeInvition", async emails => {
    const response = await api.post("v2/api/settings/credentials/invite/execute", {emails});
    return response.data;
});

const initialState = {
    credentials: {
        credentials: [],
        status: {
            type: "idle",
            error: {text: ""},
        },
    },
    invitations: {
        modified: [],
        status: {
            type: "idle",
            error: {text: ""},
        },
    },
    groups: {
        groups: [],
        status: {
            type: "idle",
            error: {text: ""},
        },
    },
};

const settingsCredentialsSlice = createSlice({
    name: "settingsCredentials",
    initialState,
    reducers: {
        setInitialState: state => initialState,
        setModifiedEmails: (state, action) => {
            state.invitations.modified = action.payload;
        },
    },
    extraReducers: {
        [fetchCredentials.pending]: state => {
            state.credentials.status.type = "loading";
        },
        [fetchCredentials.fulfilled]: (state, action) => {
            state.credentials.status.type = "succeeded";
            state.credentials.credentials = action.payload.data;
        },
        [fetchCredentials.rejected]: (state, action) => {
            state.credentials.status.type = "failed";
            state.credentials.status.error.text = action.error.message;
        },

        [addNewCredential.pending]: state => {
            state.credentials.status.type = "loading";
        },
        [addNewCredential.fulfilled]: (state, action) => {
            state.credentials.status.type = "succeeded";
            state.credentials.credentials = action.payload.data;
        },
        [addNewCredential.rejected]: (state, action) => {
            state.credentials.status.type = "failed";
            state.credentials.status.error.text = action.error.message;
        },

        [deleteCredential.pending]: state => {
            state.credentials.status.type = "loading";
        },
        [deleteCredential.fulfilled]: (state, action) => {
            state.credentials.status.type = "succeeded";
            state.credentials.credentials = action.payload.data;
        },
        [deleteCredential.rejected]: (state, action) => {
            state.credentials.status.type = "failed";
            state.credentials.status.error.text = action.error.message;
        },

        [updateCredential.pending]: state => {
            state.credentials.status.type = "loading";
        },
        [updateCredential.fulfilled]: (state, action) => {
            state.credentials.status.type = "succeeded";
            state.credentials.credentials = action.payload.data;
        },
        [updateCredential.rejected]: (state, action) => {
            state.credentials.status.type = "failed";
            state.credentials.status.error.text = action.error.message;
        },
        [fetchGroups.pending]: state => {
            state.groups.status.type = "loading";
        },
        [fetchGroups.fulfilled]: (state, action) => {
            state.groups.status.type = "succeeded";
            state.groups.groups = action.payload.data;
        },
        [fetchGroups.rejected]: (state, action) => {
            state.groups.status.type = "failed";
            state.groups.status.error.text = action.error.message;
        },
        [validateNewEmails.pending]: state => {
            state.invitations.status.type = "loading";
        },
        [validateNewEmails.fulfilled]: (state, action) => {
            state.invitations.status.type = "succeeded";
            state.invitations.modified = action.payload.data?.emails;
            state.invitations.status.error.text = action.payload.message?.[0]?.text;
        },
        [validateNewEmails.rejected]: (state, action) => {
            state.invitations.status.type = "failed";
            state.invitations.status.error.text = action.error.message;
        },
        [executeInvitaions.pending]: state => {
            state.invitations.status.type = "loading";
        },
        [executeInvitaions.fulfilled]: (state, action) => {
            state.invitations.status.type = "succeeded";
            if (action.payload.data?.length) state.credentials.credentials = action.payload.data;
            state.invitations.status.error.text = action.payload.message?.[0]?.text;
        },
        [executeInvitaions.rejected]: (state, action) => {
            state.invitations.status.type = "failed";
            state.invitations.status.error.text = action.error.message;
        },
    },
});

export const {setInitialState, setModifiedEmails} = settingsCredentialsSlice.actions;
export default settingsCredentialsSlice.reducer;

export const selectAllCredentials = state => state.settingsCredentials?.credentials?.credentials;

export const selectActiveAdminCredentials = state =>
    state.settingsCredentials.credentials?.credentials?.filter(
        cred =>
            cred?.deleted_at === null && (cred?.external_iss ||cred?.last_cognito_login) && cred?.groups?.some(group => group.group_id === 2)
    ) || [];
export const selectInactiveAdminCredentials = state =>
    state.settingsCredentials.credentials?.credentials?.filter(
        cred =>
            cred?.deleted_at !== null && (cred?.external_iss ||cred?.last_cognito_login) && cred?.groups?.some(group => group.group_id === 2)
    ) || [];
export const selectPendingAdminCredentials = state =>
    state.settingsCredentials.credentials?.credentials?.filter(
        cred =>
            !(cred?.external_iss ||cred?.last_cognito_login) && cred?.deleted_at === null && cred?.groups?.some(group => group.group_id === 2)
    ) || [];
export const selectActiveNonAdminCredentials = state =>
    state.settingsCredentials.credentials?.credentials?.filter(
        cred =>
            cred?.deleted_at === null && (cred?.external_iss ||cred?.last_cognito_login) && !cred?.groups?.some(group => group.group_id === 2)
    ) || [];
export const selectInactiveNonAdminCredentials = state =>
    state.settingsCredentials.credentials?.credentials?.filter(
        cred =>
            cred?.deleted_at !== null && (cred?.external_iss ||cred?.last_cognito_login) && !cred?.groups?.some(group => group.group_id !== 2)
    ) || [];
export const selectPendingNonAdminCredentials = state =>
    state.settingsCredentials.credentials?.credentials?.filter(
        cred =>
            !(cred?.external_iss ||cred?.last_cognito_login) && cred?.deleted_at === null && !cred?.groups?.some(group => group.group_id === 2)
    ) || [];

export const selectAllActiveCredentialsLength = state =>
    state.settingsCredentials.credentials?.credentials?.filter(
        cred => cred?.deleted_at === null && (cred?.external_iss ||cred?.last_cognito_login)
    )?.length;
export const selectAllInactiveCredentialsLength = state =>
    state.settingsCredentials.credentials?.credentials?.filter(
        cred => cred?.deleted_at !== null && (cred?.external_iss ||cred?.last_cognito_login)
    )?.length;
export const selectAllPendingCredentialsLength = state =>
    state.settingsCredentials.credentials?.credentials?.filter(
        cred => !(cred?.external_iss ||cred?.last_cognito_login) && cred?.deleted_at === null
    )?.length;

export const selectCredentialsStatus = state => state.settingsCredentials.credentials.status;
export const selectAllGroups = state => state.settingsCredentials.groups.groups;
export const selectInvitations = state => state.settingsCredentials.invitations;
export const selectGroupsStatus = state => state.settingsCredentials.groups.status;
